import { useState, useEffect } from 'react';
import { useFetcher } from '@remix-run/react';
import { FaTimes, FaEnvelope, FaUser, FaCommentDots } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

// Define the structure of the action data
interface ActionData {
  error?: string;
  success?: boolean;
  fields?: {
    name: string;
    email: string;
    message: string;
  };
}

export default function ContactModal() {
  const [isOpen, setIsOpen] = useState(false);  // State to control modal visibility
  const [modalKey, setModalKey] = useState(0); // State to reset the fetcher.Form
  const fetcher = useFetcher<ActionData>();    // Use ActionData to type fetcher
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    message: '',
  });

  const isSubmitting = fetcher.state === 'submitting';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormState((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const toggleModal = () => {
    if (!isOpen) {
      // Reset form and fetcher data when opening the modal
      setModalKey(prev => prev + 1);
      setFormState({ name: '', email: '', message: '' });
    }
    setIsOpen(!isOpen);  // Toggle modal visibility
  };

  // Close modal after successful form submission
  useEffect(() => {
    if (fetcher.data?.success) {
      const timer = setTimeout(() => {
        setIsOpen(false);
        setFormState({ name: '', email: '', message: '' }); // Reset the form fields
      }, 2500);  // Add delay to allow user to see the success message

      return () => clearTimeout(timer); // Cleanup the timer
    }
  }, [fetcher.data?.success]);

  return (
    <>
      {/* Button to Open Modal */}
      <button
        className="bg-orange-600 text-white px-4 py-2 rounded-md shadow-lg hover:bg-orange-700 transition focus:outline-none focus:ring-2 focus:ring-orange-500"
        onClick={toggleModal}  // Trigger modal toggle
      >
        Contact Me
      </button>

      {/* Modal with AnimatePresence for smooth animations */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="contact-modal-title"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 relative"
            >
              {/* Close Button */}
              <button
                className="absolute top-4 right-4 text-gray-600 hover:text-gray-800 focus:outline-none"
                onClick={toggleModal}  // Close modal on click
                aria-label="Close Modal"
              >
                <FaTimes size={20} />
              </button>

              <h2 
                id="contact-modal-title" 
                className="text-2xl font-semibold text-gray-800 text-center mb-4 flex items-center justify-center"
              >
                <FaEnvelope className="mr-2 text-orange-600" />
                Get in Touch
              </h2>

              {/* Action Feedback */}
              {fetcher.data?.error && (
                <div className="bg-red-100 text-red-700 px-4 py-2 rounded-md text-sm text-center mb-4">
                  {fetcher.data.error}
                </div>
              )}
              {fetcher.data?.success && (
                <div className="bg-green-100 text-green-700 px-4 py-2 rounded-md text-sm text-center mb-4">
                  Your message has been sent successfully!
                </div>
              )}

              {/* Contact Form */}
              <fetcher.Form 
                method="post" 
                action="/send" 
                className="space-y-4"
                key={modalKey}  // Reset form when modal opens
              >
                <div className="flex flex-col">
                  <label htmlFor="name" className="flex items-center text-sm font-medium text-gray-700">
                    <FaUser className="mr-2 text-gray-500" />
                    Name
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm transition-colors"
                    value={formState.name}
                    onChange={handleChange}
                  />
                </div>
                <div className="flex flex-col">
                  <label htmlFor="email" className="flex items-center text-sm font-medium text-gray-700">
                    <FaEnvelope className="mr-2 text-gray-500" />
                    Email
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm transition-colors"
                    value={formState.email}
                    onChange={handleChange}
                  />
                </div>
                <div className="flex flex-col">
                  <label htmlFor="message" className="flex items-center text-sm font-medium text-gray-700">
                    <FaCommentDots className="mr-2 text-gray-500" />
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    required
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm transition-colors"
                    value={formState.message}
                    onChange={handleChange}
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className={`w-full flex justify-center py-2 px-4 border border-transparent text-sm font-bold rounded-md bg-orange-600 text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 shadow-lg ${isSubmitting ? 'cursor-not-allowed opacity-50' : ''}`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </fetcher.Form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
